import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, useAppSelector } from "store";
import {
  Dialog,
  Grid,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { asyncMutations } from "store/slices/dialogs/addPart.mutations";
import { DeviceTestStatusEnum } from "../jobDetails/visit/inspection/DeviceTestStatusEnum";
import { FormattedMessage, useIntl } from "react-intl";
import { PartType, StockStore } from "operations/schema/schema";
import { getJobs, selectSelectedJob } from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { fetchRequestableParts } from "store/slices/dialogs/addPart.store";
import { flagNewDevice } from "store/slices/deviceStatus.store";

interface AddDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  loading: boolean;
}

export const AddDeviceDialog: FC<AddDeviceDialogProps> = (props) => {
  const {
    loading,
    handleClose,
    open,
  } = props;

  const [deviceName, setDeviceName] = useState("");
  const [installedDate, setInstalledDate] = useState<Date | null>(null);
  const selectedJob = useAppSelector((state) => state.jobs.selectedJobId);
  const [notes, setNotes] = useState("");
  const [testResult, setTestResult] = useState<DeviceTestStatusEnum | null>(null);
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const job = useAppSelector(selectSelectedJob);
  const [partList, setPartList] = useState<PartType[]>([]);
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const engineerName = job?.engineerName || "Unknown Engineer";
  const engineerId = job?.engineerId ? parseInt(job.engineerId, 10) : null;
  const equipmentId = job?.equipment?.id?.toString() ?? "";
  const workUnitId = job?.id ? Number(job.id.toString().split("-")[1] ?? 0) : 0;

  const handleCloseDialog = () => {
    setSelectedPart(null);
    setDeviceName('');
    setInstalledDate(null);
    setTestResult(null);
    setNotes('');
    handleClose(); 
  };

  const PREFIX = "AddPart";

  const classes = {
    flex: `${PREFIX}-flex`,
    toggleButtonGroup: `${PREFIX}-toggleButtonGroup`,
    toggleButton: `${PREFIX}-toggleButton`,
    buttonDisabled: `${PREFIX}-buttonDisabled`,
    radioButton: `${PREFIX}-radioButton`,
  };
    
  useEffect(() => {
    if (open) {
      const partQueryInput = {
        locationId: "",
        stockStore: StockStore.Engineer,
      };
      dispatch(fetchRequestableParts([partQueryInput]))
        .then((res: any) => {
          if (res?.payload) {
            setPartList(res.payload);
          }
        });
    }
  }, [open, dispatch]);

  const changeSelectedPart = (selectedPart: PartType | null) => {
    setSelectedPart(selectedPart);
  };

  const handleSubmit = async () => {
    if (!deviceName || !engineerId || !selectedPart || !selectedJob) {
      dispatch(addSnackbarMessage({ key: "AddDevice-fail" }));
      return;
    }

    const deviceData = {
      selectedPartId: Number(selectedPart.id),
      location: deviceName,
      installedDate,
      engineerName,
      workUnitId,
      engineerId,
      notes,
      testResult,
      equipmentId: equipmentId ? Number(equipmentId) : 0, 
    };
  
    try {
      const resultAction = await dispatch(asyncMutations.createDevice(deviceData));
  
      if (asyncMutations.createDevice.fulfilled.match(resultAction)) {
        const newDeviceId = resultAction.payload?.deviceId;
        if (newDeviceId) {
          dispatch(flagNewDevice(newDeviceId.toString()));
        } else {
          console.warn("AddDeviceDialog: No device ID returned after creation");
        }        
        dispatch(addSnackbarMessage({ key: "AddDevice-success" }));
        dispatch(getJobs({ force: true }));
        handleCloseDialog();
      } else {
        dispatch(addSnackbarMessage({ key: "AddDevice-fail" }));
        console.error("Failed to create device:", resultAction.payload);
      }
    } catch (err) {
      dispatch(addSnackbarMessage({ key: "AddDevice-fail" }));
      console.error("Something went wrong:", err);
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={handleCloseDialog} TransitionComponent={Transition} data-testid="addDeviceDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="back" size="large" data-testid="addDeviceDialogCloseButton">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            <FormattedMessage id="addDevice.title" /> 
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar sx={{ mb: 2 }} />
      <StyledContainer sx={{ p: 3 }}>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <div className={classes.flex}>
              <Autocomplete
                fullWidth
                data-testid="AddPart-SelectPart"
                options={partList}
                loading={loading}
                getOptionLabel={(part: PartType) => {
                  let label = part.description || '';
                  return label;
                }}
                value={selectedPart}
                onChange={(event, newValue) => changeSelectedPart(newValue)}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "part" })}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </Grid>
          <Grid item>
            <StyledTextField
              label={intl.formatMessage({ id: "addDevice.location" })}
              fullWidth
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              required
            />
          </Grid>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={enGB}>
              <DesktopDatePicker
                label={intl.formatMessage({ id: "addDevice.installedDate" })}
                value={installedDate}
                onChange={(newDate) => setInstalledDate(newDate)}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <StyledTextField
              value={notes}
              label={intl.formatMessage({ id: "addDevice.notesLabel" })}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={6}
              fullWidth
            />
          </Grid>
          <Grid item>
          <PrimaryButton
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !deviceName || 
              !selectedPart     
            }
          >
            {intl.formatMessage({ id: "general.add" })}
          </PrimaryButton>
          </Grid>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};